import { atom } from "jotai";
import { atomFamily, selectAtom } from "jotai/utils";
import type { NodeProgress, IsValidResponse } from "@/generated";
import type { Edge } from "@xyflow/react";
import type { GraphNodeV2, NodeV2Data } from "@/types/spotflow-v2";
import {
  nodesAtom,
  hoveredNodeAtom,
  hoveredPartialRunNodeAtom,
  chipHighlightNodeIdAtom,
  runNodeStatusesAtom,
  isValidDataAtom,
  renamingNodeIdAtom,
  contextMenuNodeIdAtom,
  pickerHighlightAtom,
  edgesAtom,
  edgeDropTargetNodeIdAtom,
  nodeRegistryAtom,
  selectedNodeIdAtom,
} from "../atoms";

type NodeEffectiveTypes = {
  inputs: Record<string, boolean>;
  outputs: Record<string, boolean>;
  fansOut: boolean;
};

const EMPTY_EFFECTIVE: NodeEffectiveTypes = {
  inputs: {},
  outputs: {},
  fansOut: false,
};

// ── Stable node data (ignores position-only changes) ────────────────────

/** Map of nodeId → node data. React Flow's applyNodeChanges preserves data
 *  references for position-only changes, so this map stays equal across drags
 *  and only triggers rerenders when actual node data mutates. */
export const nodeDataMapAtom = selectAtom(
  nodesAtom,
  (nodes) => {
    const map = new Map<string, NodeV2Data>();
    for (const n of nodes) {
      map.set(n.id, n.data);
    }
    return map;
  },
  mapsReferenceEqual
);

export const nodeDataByIdAtom = atomFamily((nodeId: string) =>
  atom((get) => get(nodeDataMapAtom).get(nodeId))
);

export const nodeCountAtom = selectAtom(
  nodesAtom,
  (nodes) => nodes.length,
  (a, b) => a === b
);

/** True when the selected node was removed from the canvas */
export const selectedNodeMissingAtom = atom((get) => {
  const selectedId = get(selectedNodeIdAtom);
  if (!selectedId) return false;
  return !get(nodeDataMapAtom).has(selectedId);
});

/** Structural fingerprint for validation — stable across position-only drags */
export const validationFingerprintAtom = atom((get) => {
  const dataMap = get(nodeDataMapAtom);
  const edges = get(edgesAtom);
  const nodePart = [...dataMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(
      ([id, data]) =>
        `${id}:${data.nodeType}:${JSON.stringify(data.nodeConfig ?? {})}:${JSON.stringify(data.variableBindings ?? {})}`
    )
    .join("|");
  const edgePart = edges
    .map(
      (e) =>
        `${e.source}>${e.target}:${e.sourceHandle ?? ""}>${e.targetHandle ?? ""}`
    )
    .join("|");
  return `${nodePart}#${edgePart}`;
});

/** Ancestor nodes/edges for "Run until here" hover — derived, not written by EditorV2 */
export const hoveredPartialWorkflowStructureAtom = atom((get) => {
  const hovered = get(hoveredPartialRunNodeAtom);
  if (!hovered) return undefined;

  const edges = get(edgesAtom);
  const connectedNodes: string[] = [];
  const connectedEdges: string[] = [];
  const visited = new Set<string>();
  const stack = [hovered];

  while (stack.length > 0) {
    const cur = stack.pop()!;
    if (visited.has(cur)) continue;
    visited.add(cur);
    connectedNodes.push(cur);
    for (const edge of edges) {
      if (edge.target === cur) {
        connectedEdges.push(edge.id);
        if (!visited.has(edge.source)) stack.push(edge.source);
      }
    }
  }

  return { nodes: connectedNodes, edges: connectedEdges };
});

// ── Stable node shape (RF type + parentId) ─────────────────────────────
// Captures the React-Flow-level node fields needed by selectors but excluded
// from `data` (which `nodeDataMapAtom` covers). Equality is structural, so
// position-only drag updates don't flip downstream derived atoms.
type NodeShape = { type: string; parentId: string | undefined };
const nodeShapeMapEqual = (
  a: Map<string, NodeShape>,
  b: Map<string, NodeShape>
): boolean => {
  if (a.size !== b.size) return false;
  for (const [k, va] of a) {
    const vb = b.get(k);
    if (!vb || vb.type !== va.type || vb.parentId !== va.parentId) return false;
  }
  return true;
};
export const nodeShapeMapAtom = selectAtom(
  nodesAtom,
  (nodes) => {
    const map = new Map<string, NodeShape>();
    for (const n of nodes) {
      map.set(n.id, {
        type: (n as { type?: string }).type ?? "",
        parentId: (n as { parentId?: string }).parentId,
      });
    }
    return map;
  },
  nodeShapeMapEqual
);

const graphNodesEqual = (a: GraphNodeV2[], b: GraphNodeV2[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (
      x.id !== y.id ||
      x.type !== y.type ||
      x.parentId !== y.parentId ||
      x.data !== y.data
    ) {
      return false;
    }
  }
  return true;
};

const graphNodeInputsAtom = atom((get) => ({
  dataMap: get(nodeDataMapAtom),
  shapeMap: get(nodeShapeMapAtom),
}));

/**
 * `nodesAtom` without layout. Built from the two reference-stable maps above
 * and guarded by its own equality, so it keeps its identity across drags,
 * selection and resizes and only changes when a node is added/removed/
 * re-parented or its `data` mutates. This is what per-node components and
 * per-frame hooks must subscribe to — `nodesAtom` itself is rewritten on
 * every drag frame.
 */
export const graphNodesAtom = selectAtom(
  graphNodeInputsAtom,
  ({ dataMap, shapeMap }): GraphNodeV2[] => {
    const nodes: GraphNodeV2[] = [];
    for (const [id, data] of dataMap) {
      const shape = shapeMap.get(id);
      nodes.push({ id, type: shape?.type, parentId: shape?.parentId, data });
    }
    return nodes;
  },
  graphNodesEqual
);

// ── Hover ────────────────────────────────────────────────────────────────

/** True when a node is being hovered globally (for "Run this node" highlight) */
export const globalHoveredNodeIdAtom = selectAtom(
  hoveredNodeAtom,
  (v) => v ?? null
);

/** True when THIS node is the globally-hovered node AND picker is NOT active */
export const isInputHighlightedAtom = atomFamily((nodeId: string) =>
  atom((get) => {
    const hovered = get(hoveredNodeAtom);
    const picker = get(pickerHighlightAtom);
    return !!(hovered && hovered === nodeId && !picker.targetNodeId);
  })
);

// ── Ghosting by "Run this node" hover ────────────────────────────────────

/** True when ANOTHER node is hovered (and picker not active) — this node should ghost */
export const isGhostedByHoverAtom = atomFamily((nodeId: string) =>
  atom((get) => {
    const hovered = get(hoveredNodeAtom);
    const picker = get(pickerHighlightAtom);
    return !!(hovered && hovered !== nodeId && !picker.targetNodeId);
  })
);

// ── Ghosting by "Run until here" ─────────────────────────────────────────

/** True when "Run until here" is active and this node is NOT in the ancestor set */
export const isGhostedByPartialRunAtom = atomFamily((nodeId: string) =>
  atom((get) => {
    const structure = get(hoveredPartialWorkflowStructureAtom);
    return !!(structure && !structure.nodes.includes(nodeId));
  })
);

// ── Chip highlight ───────────────────────────────────────────────────────

/** True when this node is highlighted via a variable chip hover */
export const isChipHighlightedAtom = atomFamily((nodeId: string) =>
  selectAtom(chipHighlightNodeIdAtom, (id) => id === nodeId)
);

/** True when ANY chip highlight is active but this node is NOT the highlighted one.
 *  Note: actual ghosting also depends on local isHovered + selected props,
 *  so the component still needs a small check. */
export const hasActiveChipHighlightAtom = selectAtom(
  chipHighlightNodeIdAtom,
  (id) => id !== null
);

/** The node currently highlighted as a variable source — by hovering a bound
 *  chip anywhere, or an option row in an open variable picker. Drives the
 *  off-screen arrow indicator. */
export const highlightedSourceNodeIdAtom = atom((get) => {
  const chip = get(chipHighlightNodeIdAtom);
  if (chip) return chip;
  const picker = get(pickerHighlightAtom);
  return picker.targetNodeId ? (picker.sourceNodeId ?? null) : null;
});

// ── Run status ───────────────────────────────────────────────────────────

/** This node's run progress (status, iterations, timing) */
export const nodeRunProgressAtom = atomFamily((nodeId: string) =>
  selectAtom(
    runNodeStatusesAtom,
    (statuses) => statuses[nodeId] as NodeProgress | undefined,
    (a, b) =>
      a?.status === b?.status &&
      a?.current_it === b?.current_it &&
      a?.total_it === b?.total_it &&
      a?.started_at === b?.started_at &&
      a?.finished_at === b?.finished_at
  )
);

/** V2 stores one consolidated IO record at it=1 regardless of fan-out width;
 *  current_it is display-only, never a fetch cursor. */
export const nodeIoFetchPathAtom = atomFamily((nodeId: string) =>
  atom(() => ({ effectiveNodeId: nodeId, it: 1 }))
);

// ── Validation errors ────────────────────────────────────────────────────

function selectNodeErrors(nodeId: string) {
  return (data: IsValidResponse | undefined) =>
    data?.exceptions?.[nodeId] ?? [];
}

/** Validation errors for this specific node */
export const nodeErrorsAtom = atomFamily((nodeId: string) =>
  selectAtom(isValidDataAtom, selectNodeErrors(nodeId), shallowArrayEqual)
);

// ── Renaming ─────────────────────────────────────────────────────────────

/** True when this node is being renamed */
export const isRenamingAtom = atomFamily((nodeId: string) =>
  selectAtom(renamingNodeIdAtom, (id) => id === nodeId)
);

// ── Context menu ─────────────────────────────────────────────────────────

/** True when this node's context menu is open */
export const hasContextMenuAtom = atomFamily((nodeId: string) =>
  selectAtom(contextMenuNodeIdAtom, (id) => id === nodeId)
);

// ── Picker highlight ─────────────────────────────────────────────────────

/** True when picker is active and this node is the source being browsed */
export const isPickerSourceAtom = atomFamily((nodeId: string) =>
  atom((get) => {
    const ph = get(pickerHighlightAtom);
    return !!ph.targetNodeId && ph.sourceNodeId === nodeId;
  })
);

/** True when picker is active and this node is the target being configured */
export const isPickerTargetAtom = atomFamily((nodeId: string) =>
  atom((get) => {
    const ph = get(pickerHighlightAtom);
    return !!ph.targetNodeId && ph.targetNodeId === nodeId;
  })
);

/** True when picker is active (any target selected) */
export const isPickerActiveAtom = selectAtom(
  pickerHighlightAtom,
  (ph) => !!ph.targetNodeId
);

// ── Edge drag drop target ────────────────────────────────────────────────

/** True when this node is the live drop target for an in-flight edge drag */
export const isEdgeDropTargetAtom = atomFamily((nodeId: string) =>
  selectAtom(edgeDropTargetNodeIdAtom, (id) => id === nodeId)
);

// ── Connected handles ────────────────────────────────────────────────────

/** Set of handle IDs that have an edge connected to this node */
export const nodeConnectedHandlesAtom = atomFamily((nodeId: string) =>
  selectAtom(
    edgesAtom,
    (edges) => {
      const ids = new Set<string>();
      for (const e of edges) {
        if (e.target === nodeId) ids.add(e.targetHandle ?? "input");
        if (e.source === nodeId) ids.add(e.sourceHandle ?? "output");
      }
      return ids;
    },
    setsEqual
  )
);

export const nodeEffectiveTypesAtom = atomFamily((nodeId: string) =>
  atom((get): NodeEffectiveTypes => {
    // Read from the ref-stable data map so position-only changes during a
    // node drag don't invalidate this atom (and re-render every edge).
    const dataMap = get(nodeDataMapAtom);
    const data = dataMap.get(nodeId);
    if (!data) return EMPTY_EFFECTIVE;

    const registry = get(nodeRegistryAtom);
    const registryEntry = registry.find((r) => r.type === data.nodeType);
    const inputs = (
      registryEntry?.inputs as
        | Array<{ name?: string; definition?: { is_list?: boolean } }>
        | undefined
    )?.reduce<Record<string, boolean>>((acc, input) => {
      if (input.name) acc[input.name] = input.definition?.is_list === true;
      return acc;
    }, {});
    const outputs = (
      registryEntry?.outputs as
        | Array<{ name?: string; definition?: { is_list?: boolean } }>
        | undefined
    )?.reduce<Record<string, boolean>>((acc, output) => {
      if (output.name) acc[output.name] = output.definition?.is_list === true;
      return acc;
    }, {});

    return {
      inputs: inputs ?? {},
      outputs: outputs ?? {},
      fansOut: Object.values(outputs ?? {}).some(Boolean),
    };
  })
);

/** Edges originating from this node (for output path active/inactive status) */
export const nodeSourceEdgesAtom = atomFamily((nodeId: string) =>
  selectAtom(
    edgesAtom,
    (edges) => edges.filter((e) => e.source === nodeId),
    shallowEdgeArrayEqual
  )
);

// ── Equality helpers ─────────────────────────────────────────────────────

function mapsReferenceEqual<K, V>(a: Map<K, V>, b: Map<K, V>): boolean {
  if (a.size !== b.size) return false;
  for (const [key, val] of a) {
    if (b.get(key) !== val) return false;
  }
  return true;
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) {
    if (!b.has(v)) return false;
  }
  return true;
}

function shallowArrayEqual(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function shallowEdgeArrayEqual(a: Edge[], b: Edge[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id) return false;
  }
  return true;
}
